import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Coffee, Check, Plus, Printer } from "lucide-react";
import { colors, type, spacing, radius } from "@/theme";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TEMPLATE = [
  { nome: "Bruschetta al pomodoro", prezzo: "600" },
  { nome: "Tagliatelle al ragù", prezzo: "1000" },
  { nome: "Tiramisù", prezzo: "500" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [indirizzo, setIndirizzo] = useState("");
  const [telefono, setTelefono] = useState("");
  const [righe, setRighe] = useState([{ nome: "", prezzo: "" }]);

  useEffect(() => { document.title = "Configura il menu | Tavoline"; }, []);

  const slug = user?.tenant_slug || "";
  const menuUrl = `${window.location.origin}/m/${slug}`;

  const saveDetails = async () => {
    setBusy(true); setError(null);
    try { await api.putSettings({ indirizzo: indirizzo.trim(), telefono: telefono.trim() }); setStep(2); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const addRiga = () => { if (righe.length < 4) setRighe([...righe, { nome: "", prezzo: "" }]); };
  const setRiga = (i, key, v) => { const next = [...righe]; next[i] = { ...next[i], [key]: v }; setRighe(next); };

  const useTemplate = async () => {
    setBusy(true); setError(null);
    try {
      const cat = await api.createCategory({ nome: "Menu del giorno" });
      for (const t of TEMPLATE) await api.createItem({ category_id: cat.id, nome: t.nome, prezzo_cent: parseInt(t.prezzo) });
      setStep(3);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const saveManual = async () => {
    const valid = righe.filter((r) => r.nome.trim());
    if (valid.length === 0) { setStep(3); return; }
    setBusy(true); setError(null);
    try {
      const cat = await api.createCategory({ nome: "Menu" });
      for (const r of valid) {
        const prezzo_cent = Math.round(parseFloat((r.prezzo || "0").replace(",", ".")) * 100);
        await api.createItem({ category_id: cat.id, nome: r.nome.trim(), prezzo_cent: isNaN(prezzo_cent) ? 0 : prezzo_cent });
      }
      setStep(3);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const openQr = () => window.open(api.qrPdfUrl(30), "_blank", "noopener");

  return (
    <div style={{ minHeight: "100vh", background: colors.paper }}>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", padding: `${spacing.md}px 0` }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ width: 28, height: 5, borderRadius: 3, background: i <= step ? colors.accent : colors.rule }} />)}
      </div>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md }}>
        {step === 0 && (
          <>
            <div style={heroCircle}><Coffee size={30} color={colors.accent} /></div>
            <h1 style={h}>Benvenuto, {user?.tenant_nome}</h1>
            <p style={p}>Configuriamo il tuo menu online in pochi passi. Potrai modificare tutto in seguito dal backoffice.</p>
            <button data-testid="ob-start" style={cta} onClick={() => setStep(1)}>Iniziamo</button>
          </>
        )}
        {step === 1 && (
          <>
            <h1 style={h}>I dati del locale</h1>
            <p style={p}>Servono ai clienti per trovarti. Puoi saltare e aggiungerli dopo.</p>
            <F label="Indirizzo" v={indirizzo} on={setIndirizzo} id="ob-indirizzo" />
            <F label="Telefono" v={telefono} on={setTelefono} id="ob-telefono" />
            {error ? <div style={err}>{error}</div> : null}
            <button data-testid="ob-details-next" style={cta} onClick={saveDetails} disabled={busy}>{busy ? "…" : "Continua"}</button>
            <button data-testid="ob-details-skip" onClick={() => setStep(2)} style={{ ...skip }}>Salta</button>
          </>
        )}
        {step === 2 && (
          <>
            <h1 style={h}>Aggiungi i primi piatti</h1>
            <p style={p}>Inserisci fino a 4 piatti (prezzo in euro, es. 9.50) oppure parti da un esempio.</p>
            {righe.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: spacing.sm }}>
                <input data-testid={`ob-item-nome-${i}`} style={{ ...inputStyle, flex: 2 }} value={r.nome} onChange={(e) => setRiga(i, "nome", e.target.value)} placeholder="Nome piatto" />
                <input data-testid={`ob-item-prezzo-${i}`} style={{ ...inputStyle, flex: 1 }} value={r.prezzo} onChange={(e) => setRiga(i, "prezzo", e.target.value)} placeholder="€" />
              </div>
            ))}
            {righe.length < 4 ? (
              <button data-testid="ob-add-riga" onClick={addRiga} style={linkBtn}><Plus size={16} color={colors.accent} /><span style={{ ...type.sansMedium, fontSize: 14, color: colors.accent }}>Aggiungi piatto</span></button>
            ) : null}
            {error ? <div style={err}>{error}</div> : null}
            <button data-testid="ob-items-save" style={cta} onClick={saveManual} disabled={busy}>{busy ? "…" : "Salva e continua"}</button>
            <button data-testid="ob-template" style={ghost} onClick={useTemplate} disabled={busy}>Usa un menu di esempio</button>
          </>
        )}
        {step === 3 && (
          <>
            <div style={heroCircle}><Check size={30} color={colors.accent} /></div>
            <h1 style={h}>Il tuo menu è online!</h1>
            <p style={p}>Stampa il QR code e mettilo sui tavoli o all'ingresso. I clienti inquadrano e vedono il menu.</p>
            <button data-testid="ob-qr" style={{ ...cta, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={openQr}><Printer size={18} color={colors.onAccent} />Stampa il QR code</button>
            <button data-testid="ob-view-menu" style={ghost} onClick={() => window.open(menuUrl, "_blank", "noopener")}>Vedi il menu pubblico</button>
            <button data-testid="ob-finish" style={{ ...type.sansSemi, fontSize: 15, color: colors.accent, background: "none", border: "none", height: 50 }} onClick={() => navigate("/admin")}>Vai al backoffice</button>
          </>
        )}
      </div>
    </div>
  );
}

function F({ label, v, on, id }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ ...type.sansMedium, fontSize: 13, color: colors.ink }}>{label}</label>
      <input data-testid={id} style={inputStyle} value={v} onChange={(e) => on(e.target.value)} />
    </div>
  );
}

const heroCircle = { width: 68, height: 68, borderRadius: 34, background: colors.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "center" };
const h = { ...type.serif, fontSize: 26, color: colors.ink, textAlign: "center", margin: 0 };
const p = { ...type.sans, fontSize: 14, color: colors.inkSoft, lineHeight: "21px", textAlign: "center", margin: 0 };
const inputStyle = { background: colors.surface, border: `1px solid ${colors.rule}`, borderRadius: radius.md, height: 50, padding: `0 ${spacing.md}px`, ...type.sans, fontSize: 15, color: colors.ink };
const cta = { background: colors.accent, height: 54, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansBold, fontSize: 16, marginTop: spacing.sm };
const ghost = { height: 50, borderRadius: radius.md, border: `1px solid ${colors.rule}`, background: colors.surface, color: colors.ink, ...type.sansSemi, fontSize: 15 };
const skip = { textAlign: "center", ...type.sansMedium, fontSize: 14, color: colors.inkSoft, background: "none", border: "none", padding: "8px 0" };
const err = { ...type.sansMedium, fontSize: 13, color: colors.danger, textAlign: "center" };
const linkBtn = { display: "flex", alignItems: "center", gap: 6, alignSelf: "flex-start", background: "none", border: "none", padding: "4px 0" };
